/**
 * Swimlane Layout Configuration
 * 
 * Utilities for configuring and managing 4-swimlane timeline layout
 * with proper spacing, positioning, and responsive behavior.
 */

import { ReleaseCategory, SwimLaneConfig, CategorizedRelease } from '../../types/release'
import { DEFAULT_SWIMLANE_CONFIGS } from '../../services/releaseCategorizationService'
import { calculateMultiTrackHeight, getTrackCountForCategory } from '../../utils/dualTrackPositioning'

// ============================================================================
// LAYOUT TYPES
// ============================================================================

export interface SwimlaneLayoutConfig {
  /** Total container height */
  containerHeight: number
  
  /** Height of each swimlane */
  swimlaneHeight: number
  
  /** Spacing between swimlanes */
  swimlaneSpacing: number
  
  /** Top padding */
  topPadding: number
  
  /** Bottom padding */
  bottomPadding: number
  
  /** Left padding for labels */
  leftPadding: number
  
  /** Right padding */
  rightPadding: number
  
  /** Label width */
  labelWidth: number
  
  /** Whether to show swimlane labels */
  showLabels: boolean
  
  /** Whether to show swimlane separators */
  showSeparators: boolean
}

export interface SwimlanePosition {
  /** Swimlane index (0-3) */
  index: number
  
  /** Y position from top */
  top: number
  
  /** Height of the swimlane */
  height: number
  
  /** Left position (after label) */
  left: number
  
  /** Available width for timeline content */
  width: number
  
  /** Category for this swimlane */
  category: ReleaseCategory
  
  /** Configuration for this swimlane */
  config: SwimLaneConfig
}

export interface ResponsiveBreakpoints {
  mobile: number    // < 768px
  tablet: number    // 768px - 1024px
  desktop: number   // >= 1024px
}

// ============================================================================
// DEFAULT CONFIGURATIONS
// ============================================================================

export const DEFAULT_LAYOUT_CONFIG: SwimlaneLayoutConfig = {
  containerHeight: 600, // Will be calculated dynamically based on track allocation
  swimlaneHeight: 130, // Base height, will be overridden per category
  swimlaneSpacing: 20, // Increased spacing between swimlanes
  topPadding: 15, // Added padding to prevent overlap with container boundary
  bottomPadding: 5, // Reduced to better utilize vertical space
  leftPadding: 16,
  rightPadding: 16,
  labelWidth: 100,
  showLabels: true,
  showSeparators: false
}

export const RESPONSIVE_BREAKPOINTS: ResponsiveBreakpoints = {
  mobile: 768,
  tablet: 1024,
  desktop: 1200
}

// ============================================================================
// LAYOUT CALCULATION FUNCTIONS
// ============================================================================

/**
 * Calculate swimlane positions based on layout configuration with variable heights per category
 */
export function calculateSwimlanePositions(
  layoutConfig: SwimlaneLayoutConfig,
  containerWidth: number,
  swimlaneConfigs: SwimLaneConfig[] = DEFAULT_SWIMLANE_CONFIGS
): SwimlanePosition[] {
  const positions: SwimlanePosition[] = []

  const contentLeft = layoutConfig.showLabels
    ? layoutConfig.leftPadding + layoutConfig.labelWidth
    : layoutConfig.leftPadding

  const contentWidth = containerWidth - contentLeft - layoutConfig.rightPadding

  let currentTop = layoutConfig.topPadding

  swimlaneConfigs.forEach((config, index) => {
    // Calculate height based on track allocation for this product category
    const categoryHeight = calculateMultiTrackHeight(config.category)

    positions.push({
      index,
      top: currentTop,
      height: categoryHeight,
      left: contentLeft,
      width: contentWidth,
      category: config.category,
      config
    })

    // Update top position for next swimlane
    currentTop += categoryHeight + layoutConfig.swimlaneSpacing
  })

  return positions
}

/**
 * Calculate total container height based on variable swimlane heights
 */
export function calculateTotalContainerHeight(
  layoutConfig: SwimlaneLayoutConfig,
  swimlaneConfigs: SwimLaneConfig[] = DEFAULT_SWIMLANE_CONFIGS
): number {
  let totalHeight = layoutConfig.topPadding + layoutConfig.bottomPadding

  swimlaneConfigs.forEach((config, index) => {
    const categoryHeight = calculateMultiTrackHeight(config.category)
    totalHeight += categoryHeight

    // Add spacing between swimlanes (except after the last one)
    if (index < swimlaneConfigs.length - 1) {
      totalHeight += layoutConfig.swimlaneSpacing
    }
  })

  return totalHeight
}

/**
 * Get responsive layout configuration based on container width
 */
export function getResponsiveLayoutConfig(
  containerWidth: number,
  baseConfig: SwimlaneLayoutConfig = DEFAULT_LAYOUT_CONFIG
): SwimlaneLayoutConfig {
  if (containerWidth < RESPONSIVE_BREAKPOINTS.mobile) {
    // Mobile layout
    return {
      ...baseConfig,
      swimlaneHeight: 50,
      swimlaneSpacing: 6,
      topPadding: 12,
      bottomPadding: 12,
      leftPadding: 8,
      rightPadding: 8,
      labelWidth: 80,
      showLabels: true,
      showSeparators: false
    }
  } else if (containerWidth < RESPONSIVE_BREAKPOINTS.tablet) {
    // Tablet layout
    return {
      ...baseConfig,
      swimlaneHeight: 55,
      swimlaneSpacing: 7,
      topPadding: 14,
      bottomPadding: 14,
      leftPadding: 12,
      rightPadding: 12,
      labelWidth: 90,
      showLabels: true,
      showSeparators: true
    }
  } else {
    // Desktop layout
    return baseConfig
  }
}

/**
 * Calculate total container height needed for layout
 */
export function calculateContainerHeight(
  layoutConfig: SwimlaneLayoutConfig,
  swimlaneCount: number = 4
): number {
  return layoutConfig.topPadding +
         layoutConfig.bottomPadding +
         (swimlaneCount * layoutConfig.swimlaneHeight) +
         ((swimlaneCount - 1) * layoutConfig.swimlaneSpacing)
}

/**
 * Get swimlane position for a specific category
 */
export function getSwimlanePositionForCategory(
  category: ReleaseCategory,
  positions: SwimlanePosition[]
): SwimlanePosition | undefined {
  return positions.find(pos => pos.category === category)
}

/**
 * Group releases by swimlane
 */
export function groupReleasesBySwimlane(
  releases: CategorizedRelease[]
): Record<ReleaseCategory, CategorizedRelease[]> {
  const grouped: Record<ReleaseCategory, CategorizedRelease[]> = {
    [ReleaseCategory.OSS]: [],
    [ReleaseCategory.SOFTWARE]: [],
    [ReleaseCategory.CLOUD]: [],
    [ReleaseCategory.AZURE]: []
  }
  
  releases.forEach(release => {
    if (release.category && release.isCategorized) {
      grouped[release.category].push(release)
    }
  })
  
  return grouped
}

// ============================================================================
// LAYOUT UTILITIES
// ============================================================================

/**
 * Create CSS styles for swimlane container
 */
export function createSwimlaneContainerStyles(
  layoutConfig: SwimlaneLayoutConfig
): React.CSSProperties {
  return {
    position: 'relative',
    height: calculateContainerHeight(layoutConfig),
    padding: `${layoutConfig.topPadding}px ${layoutConfig.rightPadding}px ${layoutConfig.bottomPadding}px ${layoutConfig.leftPadding}px`,
    overflow: 'hidden'
  }
}

/**
 * Create CSS styles for individual swimlane
 */
export function createSwimlaneStyles(
  position: SwimlanePosition,
  layoutConfig: SwimlaneLayoutConfig
): React.CSSProperties {
  return {
    position: 'absolute',
    top: position.top,
    left: position.left,
    width: position.width,
    height: position.height,
    backgroundColor: position.config.backgroundColor,
    borderRadius: '4px',
    border: `1px solid ${position.config.color}20`, // 20% opacity
    overflow: 'hidden'
  }
}

/**
 * Create CSS styles for swimlane label
 */
export function createSwimlaneLabelStyles(
  position: SwimlanePosition,
  layoutConfig: SwimlaneLayoutConfig
): React.CSSProperties {
  return {
    position: 'absolute',
    top: position.top,
    left: layoutConfig.leftPadding,
    width: layoutConfig.labelWidth - 8, // 8px margin
    height: position.height,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingLeft: '8px',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: position.config.textColor,
    backgroundColor: position.config.backgroundColor,
    borderRadius: '4px 0 0 4px',
    border: `1px solid ${position.config.color}40`, // 40% opacity
    borderRight: 'none'
  }
}

/**
 * Create CSS styles for swimlane separator
 */
export function createSwimlaneSeparatorStyles(
  position: SwimlanePosition,
  layoutConfig: SwimlaneLayoutConfig,
  containerWidth: number
): React.CSSProperties {
  return {
    position: 'absolute',
    top: position.top + position.height + layoutConfig.swimlaneSpacing / 2,
    left: layoutConfig.leftPadding,
    width: containerWidth - layoutConfig.leftPadding - layoutConfig.rightPadding,
    height: '1px',
    backgroundColor: '#e0e0e0',
    opacity: 0.5
  }
}

// ============================================================================
// ANIMATION AND TRANSITION UTILITIES
// ============================================================================

/**
 * Create transition styles for smooth layout changes
 */
export function createLayoutTransitionStyles(): React.CSSProperties {
  return {
    transition: 'all 0.3s ease-in-out'
  }
}

/**
 * Create hover styles for swimlanes
 */
export function createSwimlaneHoverStyles(
  position: SwimlanePosition
): React.CSSProperties {
  return {
    backgroundColor: position.config.backgroundColor,
    borderColor: position.config.color,
    transform: 'scale(1.02)',
    boxShadow: `0 2px 8px ${position.config.color}30`, // 30% opacity
    zIndex: 10
  }
}

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate layout configuration
 */
export function validateLayoutConfig(config: SwimlaneLayoutConfig): {
  isValid: boolean
  errors: string[]
  warnings: string[]
} {
  const errors: string[] = []
  const warnings: string[] = []
  
  // Required positive values
  if (config.swimlaneHeight <= 0) {
    errors.push('swimlaneHeight must be positive')
  }
  
  if (config.containerHeight <= 0) {
    errors.push('containerHeight must be positive')
  }
  
  // Reasonable ranges
  if (config.swimlaneHeight < 30) {
    warnings.push('swimlaneHeight is very small (< 30px)')
  }
  
  if (config.swimlaneHeight > 120) {
    warnings.push('swimlaneHeight is very large (> 120px)')
  }
  
  if (config.labelWidth < 50) {
    warnings.push('labelWidth is very small (< 50px)')
  }
  
  if (config.labelWidth > 200) {
    warnings.push('labelWidth is very large (> 200px)')
  }
  
  // Consistency checks
  const calculatedHeight = calculateContainerHeight(config)
  if (Math.abs(config.containerHeight - calculatedHeight) > 10) {
    warnings.push('containerHeight does not match calculated height')
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  }
}
